import { FC, InputHTMLAttributes, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import Select, { GroupBase, OptionsOrGroups, PropsValue, SingleValue } from "react-select";
import ReactInputMask from "react-input-mask";
import { differenceInCalendarDays, format, isBefore, isValid, parseISO, subMonths } from "date-fns";

import useFetch from "src/hooks/useFetch";
import { ToastContext } from "src/context/toastContext";

import { ToastTypes } from "src/types";
import { APIEndpoints } from "src/types/apiTypes";
import { NOT_TREATING_REASON } from "src/constants/cases";
import { ReRunTreatmentModalProps, RequestTreatmentNotTreatingApiResponse } from "./types";




const ReRunPatientModal: FC<ReRunTreatmentModalProps> = ({ isModalVisible, caseId, hideModal, prescriptionId, refreshListing, investigated_at, bentype,title }): JSX.Element => {
  const [treatmentDate, changeTreatmentDate] = useState<Date | null>(null);
  const [allReasons, setAllReasons] = useState<{ value: string; label: string; }[]>(NOT_TREATING_REASON[0].subReasons);
  const [cancelCategory, setCancelCategory] = useState<string>("HCP Decided Not To Treat");
  const [cancelReason, setCancelReason] = useState<string>();


  const { data: caseInsurances, mutate: getCaseInsurances } = 
    useFetch<{ insurance_name: string; is_primary: boolean; plan_type: string; }[]>(APIEndpoints.FETCH_CASE_INSURANCES, {}, { caseId }, false);

  useEffect(() => {
    if (caseId !== 0) getCaseInsurances({}, { caseId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId])


  const { loading: requestTreatmentLoading, data: requestTreatmentData, mutate: updateRequestTreatment, error: treatmentUpdateError } = useFetch<RequestTreatmentNotTreatingApiResponse>(APIEndpoints.REQUEST_TREATMENT_NOT_TREATING, {}, { caseId, prescriptionId })
  const { showToast } = useContext(ToastContext);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isInvalid, setInvalidStatus] = useState(false);

  const navigate = useNavigate();

  const submitForm = () => {
    if (!treatmentDate) {
      setInvalidStatus(true);
      showToast(["Please enter a treatment date first."], ToastTypes.WARNING);
      return;
    }
  
    setInvalidStatus(false);
  
    const requestBody = {
      prescription: {
        bentype: "medt",
        ...(typeof investigated_at === "string" ? { investigated_at } : {}),
        scheduled_date_of_service: format(treatmentDate, "yyyy-MM-dd")
      }
    };
  
    updateRequestTreatment(requestBody);
  };

  const saveTreatmentReason = () => {
    const requestBody = {
      prescription: {
        bentype: "nont",
        status_reason: cancelReason,
        status_category :cancelCategory
      }
    }
    updateRequestTreatment(requestBody)
  };
  useEffect(() => {
    if (requestTreatmentData || treatmentUpdateError) {
      //changeTreatmentDate("")
      hideModal()
      if (requestTreatmentData) {
        const { code } = requestTreatmentData;
        if (code === 200) {
          showToast([`Request Treatment Scheduled Successfully`], ToastTypes.SUCCESS);
          navigate(`/cases/${caseId}/clone`)

        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestTreatmentData, treatmentUpdateError]);



  useEffect(() => {
    if (allReasons.length > 0) {
      setCancelReason(allReasons[0].value)
    }
  }, [allReasons]);

  const getValue = () => {
    if (allReasons.length > 0) {
      const data = allReasons.find((option) => option.value === cancelReason);
      if (data) return data as unknown as PropsValue<string>;
    }
  };

  const onChangeSelect = (value: SingleValue<string>) => {
    setCancelReason((value as unknown as { value: string; label: string; }).value);
  };

  // Category related handling
  const getValueForCategory = () => {
    const data = NOT_TREATING_REASON.find((option) => option.value === cancelCategory);
    if (data) return data as unknown as PropsValue<string>;
  };

  const onChangeCategory = (value: SingleValue<string>) => {
    const selectedValue = (value as unknown as { value: string; label: string; }).value
    setCancelCategory(selectedValue);
    const data = NOT_TREATING_REASON.find((item) => item.value === selectedValue);
    if (data && data.subReasons.length > 0) { setCancelReason(""); setAllReasons(data.subReasons) }
    if (data && data.subReasons.length === 0 && allReasons.length > 0) {
      setCancelReason("")
      setAllReasons([]);
    }
  };

  // const formatDateToDDMMYYYY = (dateString:any) => {
  //   if (!dateString) return "";
  //   const [year, month, day] = dateString.split("-");
  //   return `${day}/${month}/${year}`;
  // };

  // const handleDateChange = (e:any) => {
  //   const newDate = e.target.value;
  //   changeTreatmentDate(formatDateToDDMMYYYY(newDate));
  //   setInvalidStatus(!newDate);
  // };

  const isTreatmentBeforeInvestigation = (dateToCompare: Date) => {
    if (!investigated_at) return false;
    const parsedDate = parseISO(investigated_at);
    console.log("isTreatmentBeforeInvestigation func: ", { parsedDate })
    if (!isValid(parsedDate)) return false;
    return isBefore(dateToCompare, parsedDate);
  }

  const isTreatmentMoreThan14DaysBeforeInvestigation = (dateToCompare: Date) => {
    if (!investigated_at) return false;
    const parsedDate = parseISO(investigated_at);
    console.log("isTreatmentMoreThan14DaysBeforeInvestigation func: ", { parsedDate })
    if (!isValid(parsedDate)) return false;
    return differenceInCalendarDays(parsedDate, dateToCompare) > 14;
  }

  const manageTreatmentDateBlur = (date?: Date) => {
    if (date) {
      if (isTreatmentMoreThan14DaysBeforeInvestigation(date)) {
        alert("WARNING: This date is more than 14 days before the investigation date. Please contact MQC to make this change.");
        changeTreatmentDate(null);
      } else if (isTreatmentBeforeInvestigation(date)) {
        alert("WARNING: This date is before the investigation date. Are you sure?");
      }
    } else if (treatmentDate) {
      if (isTreatmentMoreThan14DaysBeforeInvestigation(treatmentDate)) {
        alert("WARNING: This date is more than 14 days before the investigation date. Please contact MQC to make this change.");
        changeTreatmentDate(null);
      } else if (isTreatmentBeforeInvestigation(treatmentDate)) {
        alert("WARNING: This date is before the investigation date. Are you sure?");
      }
    }
  }

  return (
    <div
      className={`modal fade ${isModalVisible ? 'show show-modal' : ''}`}
      id="exampleModal"
      tabIndex={-1}
      data-bs-backdrop="true"
      aria-hidden="true"
      onClick={hideModal}
    >
      <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <p className="modal-title text-success fw-semibold">
              {`Case -${caseId} ${title}`}
            </p>
            <button type="button" className="btn-close" aria-label="Close" onClick={hideModal}></button>
          </div>

          <div className="modal-body">
            <p className="mb-3">
              The previous case for this patient does not have a treatment date entered or a reason for not being treated.
            </p>

            <p className="text-start">
              1. If you would like to enter a treatment date for this patient, Please enter it below.{' '}
              <span className="text-danger" style={{ fontWeight: 800 }}>OR</span>,
            </p>
            <div className="row mx-3 align-items-center mb-3">
              <div className="col-3 text-end">
                <label htmlFor="treatmentDate" className="form-label"> Date * </label>
              </div>

              <div className="col-6">
              <DatePicker
                selected={treatmentDate}
                onChange={(date) => {
                  changeTreatmentDate(date);
                  if (date) manageTreatmentDateBlur(date);
                  setInvalidStatus(false); // clear error when user picks a date
                }}
                dateFormat="MM/dd/yyyy"
                className={`form-control ${isInvalid ? "is-invalid" : ""}`}
                placeholderText="mm/dd/yyyy"
                showMonthDropdown
                showYearDropdown
                customInput={
                  <ReactInputMask mask="99/99/9999">
                    {(inputProps: InputHTMLAttributes<HTMLInputElement>) => (
                      <input
                        {...inputProps}
                      />
                    )}
                  </ReactInputMask>
                }
                minDate={subMonths(new Date(), 6)}
                maxDate={new Date()}
                wrapperClassName="w-100"
                onBlur={manageTreatmentDateBlur as unknown as React.FocusEventHandler<HTMLDivElement>}
              />
              {isInvalid && (
                <p className="mb-0 text-danger pt-1 text-start w-100">Date is required</p>
              )}


              </div> 
              <div className="col-2">
                <button disabled={requestTreatmentLoading} type="button" className="btn btn-blue px-4" data-bs-dismiss="modal"
                  onClick={submitForm}>
                  {/* {requestTreatmentLoading ? (<i className="spinner-border me-2"></i>) : null} */}
                  Save
                </button>

              </div>
            </div>
            <p className="text-start">2. Please specify reason for not being treated.</p>

            <div className="row align-items-center gx-2">
              <div className="col-md-5 col-12">
                <label htmlFor="cancelTreatmentCategory" className="form-label justify-content-center mb-1">Status Category *</label>
                <Select
                  options={NOT_TREATING_REASON as unknown as OptionsOrGroups<string, GroupBase<string>>}
                  id="cancelTreatmentCategory"
                  isSearchable={false}
                  noOptionsMessage={() => "Select Option..."}
                  value={getValueForCategory()}
                  onChange={(value) => onChangeCategory(value)}
                />
              </div>
              {allReasons.length > 0 && (
                <div className="col-md-5 col-12">
                  <label htmlFor="cancelTreatment" className="form-label justify-content-center mb-1"> Status Reason *</label>
                  <Select
                    options={allReasons as unknown as OptionsOrGroups<string, GroupBase<string>>}
                    id="cancelTreatment"
                    isSearchable={false}
                    noOptionsMessage={() => "Select Option..."}
                    value={getValue()}
                    onChange={(value) => onChangeSelect(value)}
                  />
                </div>
              )}
              <div className="col-md-2 col-12 pt-4">
                <button disabled={requestTreatmentLoading} type="button" className="btn btn-blue" data-bs-dismiss="modal" onClick={saveTreatmentReason}>
                  {/* {requestTreatmentLoading ? (<i className="spinner-border me-2"></i>) : null} */}
                  Not Treating
                </button>
              </div>
            </div>

            {cancelCategory === "HCP Decided Not To Treat" && 
              cancelReason === "Cost to Patient" &&
              caseInsurances?.length === 1 &&
              caseInsurances?.some((insurance) => insurance.plan_type?.toLowerCase()?.includes("commercial")) ? (
              <div className="alert alert-warning mt-3">
                <span style={{ fontSize: 14 }}>
                  Patient could be eligible for cost savings based on their plan type. Please 
                  visit 
                  <a className="px-1" href="https://www.qutenza.com/save-on-qutenza-treatment/" target="_blank" rel="noreferrer">
                    QUTENZA (Capsaicin) | Financial Support
                  </a>
                  for more details. Are you sure you want to save this 'Not Treated' status?
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReRunPatientModal;
